import { Connection } from "@xyflow/react"
import { UbiverseNode, UbiverseEdge, UbiverseSequenceNote, UbiverseSequence } from '../types'

export interface IGlobalState {
  control: {
    isPlaying: boolean
    playheadTime: number
    playheadPosition: {
      bar: number
      beat: number
    }
    repeat: {
      isActive: boolean
      from: {
        bar: number
        beat: number
      }
      to: {
        bar: number
        beat: number
      }
    }
    bpm: number
    signature: {
      numerator: number
      denominator: number
    }
  },
  editor: {
    targetId: string,
    quantization: {
      isActive: boolean,
      numerator: number,
      denominator: number
    },
    timeline: {
      zoom: number
    }
  },
  nodes: UbiverseNode[],
  edges: UbiverseEdge[],
  // sequences: UbiverseSequenceNote[]
  sequences: UbiverseSequence[]
}

export const GlobalActionTypeList = {
  SetControlIsPlaying: 'SET_CONTROL_IS_PLAYING',
  SetControlPlayheadTime: 'SET_CONTROL_PLAYHEAD_TIME',
  SetControlPlayheadPosition: 'SET_CONTROL_PLAYHEAD_POSITION',
  SetControlBpm: 'SET_CONTROL_BPM',
  SetControlSignatureNumerator: 'SET_CONTROL_SIGNATURE__NUMERATOR',
  SetControlSignatureDenominator: 'SET_CONTROL_SIGNATURE__DENOMINATOR',
  SetControlRepeatIsActive: 'SET_CONTROL_REPEAT_IS_ACTIVE',
  SetControlRepeatFrom: 'SET_CONTROL_REPEAT_FROM',
  SetControlRepeatTo: 'SET_CONTROL_REPEAT_TO',
  SetEditorTargetId: 'SET_EDITOR_TARGET_ID',
  SetEditorQuantizationNumerator: 'SET_EDITOR_QUANTIZATION_NUMERATOR',
  SetEditorQuantizationDenominator: 'SET_EDITOR_QUANTIZATION_DENOMINATOR',
  SetEditorQuantizationIsActive: 'SET_EDITOR_QUANTIZATION_IS_ACTIVE',
  SetEditorTimelineZoom: 'SET_EDITOR_TIMELINE_ZOOM',
  SetNodes: 'SET_NODES',
  SetEdges: 'SET_EDGES',
  AddNode: 'ADD_NODE',
  AddEdge: 'ADD_EDGE',
  UpdateNodeById: 'UPDATE_NODE_BY_ID',
  RemoveNode: 'REMOVE_NODE',
  RemoveEdge: 'REMOVE_EDGE',
  UpdateNodes: 'UPDATE_NODES',
  UpdateEdges: 'UPDATE_EDGES',
  AddSequence: 'ADD_SEQUENCE',
  UpdateSequenceById: 'UPDATE_SEQUENCE_BY_ID',
  RemoveSequence: 'REMOVE_SEQUENCE',
  UpdateSequences: 'UPDATE_SEQUENCES',
  AddSequenceNote: 'ADD_SEQUENCE_NOTE',
  UpdateSequenceNoteById: 'UPDATE_SEQUENCE_NOTE_BY_ID',
  RemoveSequenceNote: 'REMOVE_SEQUENCE_NOTE',
  UpdateSequenceNotes: 'UPDATE_SEQUENCE_NOTES',
  SetAll: 'SET_ALL'
} as const;

export type GlobalActionType = typeof GlobalActionTypeList[keyof typeof GlobalActionTypeList];

export type GlobalAction =
  | { type: typeof GlobalActionTypeList.SetControlIsPlaying; payload: boolean }
  | { type: typeof GlobalActionTypeList.SetControlPlayheadTime; payload: number }
  | { type: typeof GlobalActionTypeList.SetControlPlayheadPosition; payload: { bar: number; beat: number } }
  | { type: typeof GlobalActionTypeList.SetControlBpm; payload: number }
  | { type: typeof GlobalActionTypeList.SetControlSignatureNumerator; payload: number }
  | { type: typeof GlobalActionTypeList.SetControlSignatureDenominator; payload: number }
  | { type: typeof GlobalActionTypeList.SetControlRepeatIsActive; payload: boolean }
  | { type: typeof GlobalActionTypeList.SetControlRepeatFrom; payload: { bar: number; beat: number } }
  | { type: typeof GlobalActionTypeList.SetControlRepeatTo; payload: { bar: number; beat: number } }
  | { type: typeof GlobalActionTypeList.SetEditorTargetId; payload: string }
  | { type: typeof GlobalActionTypeList.SetEditorQuantizationIsActive; payload: boolean }
  | { type: typeof GlobalActionTypeList.SetEditorQuantizationNumerator; payload: number }
  | { type: typeof GlobalActionTypeList.SetEditorQuantizationDenominator; payload: number }
  | { type: typeof GlobalActionTypeList.SetEditorTimelineZoom; payload: number }
  | { type: typeof GlobalActionTypeList.SetNodes; payload: UbiverseNode[] }
  | { type: typeof GlobalActionTypeList.SetEdges; payload: UbiverseEdge[] }
  | { type: typeof GlobalActionTypeList.AddNode; payload: UbiverseNode }
  | { type: typeof GlobalActionTypeList.AddEdge; payload: Connection }
  | { type: typeof GlobalActionTypeList.UpdateNodeById; payload: UbiverseNode }
  | { type: typeof GlobalActionTypeList.RemoveNode; payload: string }
  | { type: typeof GlobalActionTypeList.RemoveEdge; payload: string }
  | { type: typeof GlobalActionTypeList.UpdateNodes; payload: any }
  | { type: typeof GlobalActionTypeList.UpdateEdges; payload: any }
  | { type: typeof GlobalActionTypeList.AddSequence; payload: UbiverseSequence }
  | { type: typeof GlobalActionTypeList.UpdateSequenceById; payload: UbiverseSequence }
  | { type: typeof GlobalActionTypeList.RemoveSequence; payload: string }
  | { type: typeof GlobalActionTypeList.UpdateSequences; payload: UbiverseSequence[] }
  | { type: typeof GlobalActionTypeList.AddSequenceNote; payload: { sequenceId: string, note: UbiverseSequenceNote } }
  | { type: typeof GlobalActionTypeList.UpdateSequenceNoteById; payload: { sequenceId: string, note: UbiverseSequenceNote } }
  | { type: typeof GlobalActionTypeList.RemoveSequenceNote; payload: { sequenceId: string, noteId: string } }
  | { type: typeof GlobalActionTypeList.UpdateSequenceNotes; payload: { sequenceId: string, notes: UbiverseSequenceNote[] } }
  | { type: typeof GlobalActionTypeList.SetAll; payload: IGlobalState }